import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import TagList from './cards/TagList';
import SearchableSelect from './SearchableSelect';
import { useUserSearch } from '../hooks/useUserSearch';
import { useServerStats } from '../contexts/ServerStatsContext';
import { useAuth } from '../contexts/AuthContext';

// Fixed palette for the per-tenant badge on the Cross-tenant access tab.
// A tenant is hashed to one of these rather than hand-assigned, so a brand
// new tenant gets a stable, distinct color with no code change needed.
const TENANT_BADGE_COLORS = [
  'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
  'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300',
  'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300',
];

function tenantBadgeColor(tenant: string): string {
  let hash = 0;
  for (let i = 0; i < tenant.length; i++) {
    hash = (hash * 31 + tenant.charCodeAt(i)) >>> 0;
  }
  return TENANT_BADGE_COLORS[hash % TENANT_BADGE_COLORS.length];
}

interface OwnershipRecord {
  resource_type: 'server' | 'agent';
  resource_path: string;
  owner_username: string;
  owner_pending?: boolean;
  owner_team: string | null;
  backup_owner: string | null;
  transferred_from: string | null;
  transferred_at: string | null;
  created_at: string;
  updated_at: string;
}

interface GovernanceAuditEntry {
  entry_id: string;
  event_type: string;
  resource_type: 'server' | 'agent';
  resource_path: string;
  actor: string;
  timestamp: string;
  details: Record<string, unknown>;
}

type PolicyRuleType =
  | 'naming'
  | 'metadata_required'
  | 'approval_required_for'
  | 'group_auto_mapping';
type PolicyEnforcementMode = 'block' | 'review';

interface GovernancePolicy {
  policy_id: string;
  name: string;
  enabled: boolean;
  rule_type: PolicyRuleType;
  enforcement_mode: PolicyEnforcementMode;
  config: Record<string, unknown>;
  created_by: string;
  created_at: string;
  updated_at: string;
}

type ApprovalRequestStatus = 'pending' | 'approved' | 'rejected' | 'withdrawn';

interface ApprovalRequest {
  request_id: string;
  resource_type: 'server' | 'agent';
  resource_path: string;
  requested_by: string;
  requested_at: string;
  status: ApprovalRequestStatus;
  decided_by: string | null;
  decided_at: string | null;
  decision_reason: string | null;
  payload_snapshot: { tags?: string[] } | null;
}

interface CrossTenantGrantSummary {
  user_id: string;
  user_tenant_label: string | null;
  resource_path: string;
  server_name: string | null;
  server_tenant_id: string | null;
  num_tools: number;
  reason: string;
  granted_by: string;
  granted_at: string;
  last_used_at: string | null;
}

interface GovernanceDashboardProps {
  onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

/**
 * Governance Dashboard (AGENTGOV-71, Phase 2 + 3 + 4).
 *
 * Ownership lookup/transfer, the governance audit trail, governance policy
 * management, and the approval queue. A registration only actually lands in
 * the queue once the backend's governance_approval_workflow_enabled flag is
 * set AND an enabled 'approval_required_for' policy matches it.
 */
const GovernanceDashboard: React.FC<GovernanceDashboardProps> = ({ onShowToast }) => {
  const { user } = useAuth();
  const isAdmin = user?.is_admin === true;

  const [activeTab, setActiveTab] = useState<
    'ownership' | 'audit' | 'policies' | 'approvals' | 'cross-tenant'
  >(isAdmin ? 'ownership' : 'approvals');

  // Ownership lookup state
  const [lookupType, setLookupType] = useState<'server' | 'agent'>('server');
  const [lookupPath, setLookupPath] = useState('');
  const [ownership, setOwnership] = useState<OwnershipRecord | null>(null);
  const [ownershipLoading, setOwnershipLoading] = useState(false);
  const [ownershipError, setOwnershipError] = useState<string | null>(null);
  // True when the last lookup completed with a 404 -- the resource predates
  // the governance feature and has no ownership record yet. The transfer
  // form is still shown in this case so an owner can be assigned for the
  // first time, creating the record.
  const [lookupHasNoRecord, setLookupHasNoRecord] = useState(false);

  // Transfer form state
  const [newOwner, setNewOwner] = useState('');
  const [newTeam, setNewTeam] = useState('');
  const [transferring, setTransferring] = useState(false);
  const { users: iamUsers, isLoading: iamUsersLoading } = useUserSearch();
  const newOwnerOptions = (iamUsers || []).map((u) => ({
    value: u.username,
    label: u.username,
    description: u.email,
  }));

  // All-servers-with-owner table
  const { servers, loading: serversLoading } = useServerStats();
  const [ownerFilter, setOwnerFilter] = useState('');
  const serverRows = (servers || [])
    .filter((s) => s.type === 'server')
    .filter((s) => {
      if (!ownerFilter.trim()) return true;
      const q = ownerFilter.trim().toLowerCase();
      return (s.owner || s.registered_by || '').toLowerCase().includes(q);
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  // Audit trail state
  const [auditEntries, setAuditEntries] = useState<GovernanceAuditEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [auditResourcePath, setAuditResourcePath] = useState('');
  const [auditEventType, setAuditEventType] = useState('');

  // Policies state
  const [policies, setPolicies] = useState<GovernancePolicy[]>([]);
  const [policiesLoading, setPoliciesLoading] = useState(false);
  const [policiesError, setPoliciesError] = useState<string | null>(null);
  const [newPolicyName, setNewPolicyName] = useState('');
  const [newPolicyRuleType, setNewPolicyRuleType] = useState<PolicyRuleType>('naming');
  const [newPolicyEnforcementMode, setNewPolicyEnforcementMode] =
    useState<PolicyEnforcementMode>('review');
  const [newPolicyPattern, setNewPolicyPattern] = useState('^[a-z0-9]+(-[a-z0-9]+)*$');
  const [newPolicyRequiredFields, setNewPolicyRequiredFields] = useState('description');
  const [newPolicyResourceTypes, setNewPolicyResourceTypes] = useState('');
  const [newPolicyMatchTags, setNewPolicyMatchTags] = useState('');
  const [newPolicyTargetGroup, setNewPolicyTargetGroup] = useState('');
  const [newPolicyShareable, setNewPolicyShareable] = useState(false);
  const [creatingPolicy, setCreatingPolicy] = useState(false);

  // Approval queue state
  const [approvalRequests, setApprovalRequests] = useState<ApprovalRequest[]>([]);
  const [approvalsLoading, setApprovalsLoading] = useState(false);
  const [approvalsError, setApprovalsError] = useState<string | null>(null);
  const [decidingRequestId, setDecidingRequestId] = useState<string | null>(null);
  const [rejectReasons, setRejectReasons] = useState<Record<string, string>>({});

  // Cross-tenant grants state (AGENTGOV-190 all-up view)
  const [crossTenantGrants, setCrossTenantGrants] = useState<CrossTenantGrantSummary[]>([]);
  const [crossTenantLoading, setCrossTenantLoading] = useState(false);
  const [crossTenantError, setCrossTenantError] = useState<string | null>(null);
  const [crossTenantFilter, setCrossTenantFilter] = useState('');
  const [revokingGrantKey, setRevokingGrantKey] = useState<string | null>(null);
  // Which tenant section's inline "Add grant" form is open, or null when
  // none is. Scoped per tenant (not one global form) so the server picker
  // only ever shows that tenant's own servers -- there is no ambiguity
  // about which company's wall the new grant crosses into.
  const [addGrantTenant, setAddGrantTenant] = useState<string | null>(null);
  const [grantServerPath, setGrantServerPath] = useState('');
  const [grantUserId, setGrantUserId] = useState('');
  const [grantTenantLabel, setGrantTenantLabel] = useState('');
  const [grantReason, setGrantReason] = useState('');
  const [isSavingGrant, setIsSavingGrant] = useState(false);

  const performLookup = useCallback(async (resourceType: 'server' | 'agent', resourcePath: string) => {
    if (!resourcePath.trim()) {
      onShowToast('Enter a resource path to look up', 'info');
      return;
    }
    setOwnershipLoading(true);
    setOwnershipError(null);
    setOwnership(null);
    setLookupHasNoRecord(false);
    setNewOwner('');
    setNewTeam('');
    try {
      const response = await axios.get<OwnershipRecord>('/api/governance/ownership', {
        params: { resource_type: resourceType, resource_path: resourcePath.trim() },
      });
      setOwnership(response.data);
      setNewTeam(response.data.owner_team || '');
    } catch (err: any) {
      if (err.response?.status === 404) {
        setOwnershipError(
          `No ownership record found for this ${resourceType} yet. Assign an owner below to create one.`
        );
        setLookupHasNoRecord(true);
      } else {
        setOwnershipError(err.response?.data?.detail || 'Failed to look up ownership.');
      }
    } finally {
      setOwnershipLoading(false);
    }
  }, [onShowToast]);

  const handleLookup = useCallback(() => {
    performLookup(lookupType, lookupPath);
  }, [performLookup, lookupType, lookupPath]);

  const handleTransfer = useCallback(async () => {
    if (!lookupPath.trim() || !newOwner.trim()) {
      onShowToast('Enter a new owner username', 'info');
      return;
    }
    setTransferring(true);
    try {
      const response = await axios.post<OwnershipRecord>('/api/governance/ownership/transfer', {
        resource_type: ownership?.resource_type || lookupType,
        resource_path: ownership?.resource_path || lookupPath.trim(),
        new_owner_username: newOwner.trim(),
        new_owner_team: newTeam.trim() || null,
      });
      setOwnership(response.data);
      setOwnershipError(null);
      setLookupHasNoRecord(false);
      setNewOwner('');
      onShowToast(`Ownership transferred to ${response.data.owner_username}`, 'success');
    } catch (err: any) {
      onShowToast(err.response?.data?.detail || 'Failed to transfer ownership.', 'error');
    } finally {
      setTransferring(false);
    }
  }, [ownership, newOwner, newTeam, onShowToast, lookupType, lookupPath]);

  const fetchAudit = useCallback(async () => {
    setAuditLoading(true);
    setAuditError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (auditResourcePath.trim()) params.set('resource_path', auditResourcePath.trim());
      if (auditEventType) params.set('event_type', auditEventType);
      const response = await axios.get<{ entries: GovernanceAuditEntry[]; count: number }>(
        `/api/governance/audit?${params.toString()}`
      );
      setAuditEntries(response.data.entries);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setAuditError('Access denied. Admin permissions required to view the governance audit trail.');
      } else {
        setAuditError(err.response?.data?.detail || 'Failed to load governance audit trail.');
      }
    } finally {
      setAuditLoading(false);
    }
  }, [auditResourcePath, auditEventType]);

  const fetchPolicies = useCallback(async () => {
    setPoliciesLoading(true);
    setPoliciesError(null);
    try {
      const response = await axios.get<{ policies: GovernancePolicy[]; count: number }>(
        '/api/governance/policies'
      );
      setPolicies(response.data.policies);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setPoliciesError('Access denied. You need the manage_governance_policy permission.');
      } else {
        setPoliciesError(err.response?.data?.detail || 'Failed to load governance policies.');
      }
    } finally {
      setPoliciesLoading(false);
    }
  }, []);

  const handleCreatePolicy = useCallback(async () => {
    if (!newPolicyName.trim()) {
      onShowToast('Enter a policy name', 'info');
      return;
    }
    if (
      newPolicyRuleType === 'group_auto_mapping' &&
      (!newPolicyMatchTags.trim() || !newPolicyTargetGroup.trim())
    ) {
      onShowToast('Enter at least one match tag and a target group', 'info');
      return;
    }
    const config =
      newPolicyRuleType === 'naming'
        ? { pattern: newPolicyPattern.trim() }
        : newPolicyRuleType === 'metadata_required'
        ? { required_fields: newPolicyRequiredFields.split(',').map((f) => f.trim()).filter(Boolean) }
        : newPolicyRuleType === 'group_auto_mapping'
        ? {
            match_tags: newPolicyMatchTags.split(',').map((t) => t.trim()).filter(Boolean),
            target_group: newPolicyTargetGroup.trim(),
            shareable: newPolicyShareable,
          }
        : {
            resource_types: newPolicyResourceTypes
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean),
          };

    setCreatingPolicy(true);
    try {
      await axios.post('/api/governance/policies', {
        name: newPolicyName.trim(),
        enabled: true,
        rule_type: newPolicyRuleType,
        enforcement_mode: newPolicyEnforcementMode,
        config,
      });
      setNewPolicyName('');
      onShowToast('Governance policy created', 'success');
      fetchPolicies();
    } catch (err: any) {
      onShowToast(err.response?.data?.detail || 'Failed to create governance policy.', 'error');
    } finally {
      setCreatingPolicy(false);
    }
  }, [newPolicyName, newPolicyRuleType, newPolicyEnforcementMode, newPolicyPattern, newPolicyRequiredFields, newPolicyResourceTypes, newPolicyMatchTags, newPolicyTargetGroup, newPolicyShareable, onShowToast, fetchPolicies]);

  const handleTogglePolicy = useCallback(async (policy: GovernancePolicy) => {
    try {
      await axios.patch(`/api/governance/policies/${policy.policy_id}`, {
        enabled: !policy.enabled,
      });
      fetchPolicies();
    } catch (err: any) {
      onShowToast(err.response?.data?.detail || 'Failed to update governance policy.', 'error');
    }
  }, [onShowToast, fetchPolicies]);

  const handleDeletePolicy = useCallback(async (policy: GovernancePolicy) => {
    try {
      await axios.delete(`/api/governance/policies/${policy.policy_id}`);
      onShowToast('Governance policy deleted', 'success');
      fetchPolicies();
    } catch (err: any) {
      onShowToast(err.response?.data?.detail || 'Failed to delete governance policy.', 'error');
    }
  }, [onShowToast, fetchPolicies]);

  const fetchApprovals = useCallback(async () => {
    setApprovalsLoading(true);
    setApprovalsError(null);
    try {
      const response = await axios.get<{ requests: ApprovalRequest[]; count: number }>(
        '/api/governance/approvals',
        { params: { status: 'pending' } }
      );
      setApprovalRequests(response.data.requests);
    } catch (err: any) {
      setApprovalsError(err.response?.data?.detail || 'Failed to load the approval queue.');
    } finally {
      setApprovalsLoading(false);
    }
  }, []);

  const fetchCrossTenantGrants = useCallback(async () => {
    setCrossTenantLoading(true);
    setCrossTenantError(null);
    try {
      const response = await axios.get<CrossTenantGrantSummary[]>(
        '/api/governance/cross-tenant-grants'
      );
      setCrossTenantGrants(response.data);
    } catch (err: any) {
      setCrossTenantError(
        err.response?.data?.detail || 'Failed to load cross-tenant grants.'
      );
    } finally {
      setCrossTenantLoading(false);
    }
  }, []);

  // Server options for the currently-open tenant's add-grant form, scoped to
  // that tenant's own servers only -- picking a server already fixes which
  // tenant's wall the grant crosses into, so there is nothing left to
  // mis-target.
  const grantServerOptions = (servers || [])
    .filter((s) => s.type === 'server' && (s.tenant_id || 'Unknown') === addGrantTenant)
    .map((s) => ({ value: s.path, label: s.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleAddCrossTenantGrant = useCallback(async () => {
    if (!grantServerPath.trim() || !grantUserId.trim() || !grantReason.trim()) {
      return;
    }
    setIsSavingGrant(true);
    try {
      await axios.post(
        `/api/servers/${grantServerPath.trim().replace(/^\//, '')}/cross-tenant-grants`,
        {
          user_id: grantUserId.trim(),
          reason: grantReason.trim(),
          user_tenant_label: grantTenantLabel.trim() || null,
        }
      );
      onShowToast(`Granted ${grantUserId.trim()} access to ${grantServerPath.trim()}`, 'success');
      setGrantServerPath('');
      setGrantUserId('');
      setGrantTenantLabel('');
      setGrantReason('');
      setAddGrantTenant(null);
      fetchCrossTenantGrants();
    } catch (err: any) {
      onShowToast(err.response?.data?.detail || 'Failed to create grant.', 'error');
    } finally {
      setIsSavingGrant(false);
    }
  }, [grantServerPath, grantUserId, grantTenantLabel, grantReason, onShowToast, fetchCrossTenantGrants]);

  const handleRevokeCrossTenantGrant = useCallback(
    async (grant: CrossTenantGrantSummary) => {
      const key = `${grant.user_id}::${grant.resource_path}`;
      setRevokingGrantKey(key);
      try {
        await axios.delete(
          `/api/servers/${grant.resource_path}/cross-tenant-grants/${grant.user_id}`
        );
        onShowToast(
          `Revoked ${grant.user_id}'s access to ${grant.server_name || grant.resource_path}`,
          'success'
        );
        fetchCrossTenantGrants();
      } catch (err: any) {
        onShowToast(err.response?.data?.detail || 'Failed to revoke grant.', 'error');
      } finally {
        setRevokingGrantKey(null);
      }
    },
    [onShowToast, fetchCrossTenantGrants]
  );

  const handleApprove = useCallback(async (request: ApprovalRequest) => {
    setDecidingRequestId(request.request_id);
    try {
      await axios.post(`/api/governance/approvals/${request.request_id}/approve`, {
        reason: rejectReasons[request.request_id] || null,
      });
      onShowToast(`Approved ${request.resource_type} '${request.resource_path}'`, 'success');
      fetchApprovals();
    } catch (err: any) {
      onShowToast(err.response?.data?.detail || 'Failed to approve request.', 'error');
    } finally {
      setDecidingRequestId(null);
    }
  }, [rejectReasons, onShowToast, fetchApprovals]);

  const handleReject = useCallback(async (request: ApprovalRequest) => {
    const reason = rejectReasons[request.request_id];
    if (!reason || !reason.trim()) {
      onShowToast('Enter a reason before rejecting', 'info');
      return;
    }
    setDecidingRequestId(request.request_id);
    try {
      await axios.post(`/api/governance/approvals/${request.request_id}/reject`, { reason });
      onShowToast(`Rejected ${request.resource_type} '${request.resource_path}'`, 'success');
      fetchApprovals();
    } catch (err: any) {
      onShowToast(err.response?.data?.detail || 'Failed to reject request.', 'error');
    } finally {
      setDecidingRequestId(null);
    }
  }, [rejectReasons, onShowToast, fetchApprovals]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  useEffect(() => {
    if (activeTab === 'audit') {
      fetchAudit();
    } else if (activeTab === 'policies') {
      fetchPolicies();
    } else if (activeTab === 'approvals') {
      fetchApprovals();
    } else if (activeTab === 'cross-tenant') {
      fetchCrossTenantGrants();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Governance</h2>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-6">
          {isAdmin && (
          <button
            onClick={() => setActiveTab('ownership')}
            className={`py-2 px-1 border-b-2 text-sm font-medium ${
              activeTab === 'ownership'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            Ownership
          </button>
          )}
          <button
            onClick={() => setActiveTab('audit')}
            className={`py-2 px-1 border-b-2 text-sm font-medium ${
              activeTab === 'audit'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            Governance Audit
          </button>
          <button
            onClick={() => setActiveTab('policies')}
            className={`py-2 px-1 border-b-2 text-sm font-medium ${
              activeTab === 'policies'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            Policies
          </button>
          <button
            onClick={() => setActiveTab('approvals')}
            className={`flex items-center gap-1.5 py-2 px-1 border-b-2 text-sm font-medium ${
              activeTab === 'approvals'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            <span>Approval Queue</span>
            {approvalRequests.length > 0 && (
              <span
                className="flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white"
                title={`${approvalRequests.length} pending approval${approvalRequests.length === 1 ? '' : 's'}`}
              >
                {approvalRequests.length > 99 ? '99+' : approvalRequests.length}
              </span>
            )}
          </button>
          {isAdmin && (
          <button
            onClick={() => setActiveTab('cross-tenant')}
            className={`py-2 px-1 border-b-2 text-sm font-medium ${
              activeTab === 'cross-tenant'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            Cross-tenant access
          </button>
          )}
        </nav>
      </div>

      {activeTab === 'ownership' && !isAdmin && (
        <div className="text-sm text-red-600 dark:text-red-400">
          Admin access required to view or manage ownership.
        </div>
      )}

      {activeTab === 'ownership' && isAdmin && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between gap-3 p-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                All servers ({serverRows.length})
              </h3>
              <input
                type="text"
                value={ownerFilter}
                onChange={(e) => setOwnerFilter(e.target.value)}
                placeholder="Filter by owner..."
                className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm"
              />
            </div>
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-500 dark:text-gray-400">Server</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500 dark:text-gray-400">Owner</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500 dark:text-gray-400">Registered by</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {serversLoading && (
                    <tr>
                      <td colSpan={4} className="px-3 py-3 text-gray-500 dark:text-gray-400">
                        Loading servers...
                      </td>
                    </tr>
                  )}
                  {!serversLoading && serverRows.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-3 py-3 text-gray-500 dark:text-gray-400">
                        No servers match.
                      </td>
                    </tr>
                  )}
                  {serverRows.map((s) => (
                    <tr
                      key={s.path}
                      className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                        {s.name}
                        <div className="text-xs text-gray-400">{s.path}</div>
                      </td>
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                        {s.owner || <span className="text-gray-400">unowned</span>}
                        {s.owner && s.owner_pending && (
                          <span
                            className="ml-1.5 inline-block rounded bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400"
                            title="This owner hasn't signed in yet -- ownership will finalize automatically once they do."
                          >
                            pending sign-in
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                        {s.registered_by || '-'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => {
                            setLookupType('server');
                            setLookupPath(s.path);
                            performLookup('server', s.path);
                          }}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Type
              </label>
              <select
                value={lookupType}
                onChange={(e) => setLookupType(e.target.value as 'server' | 'agent')}
                className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm"
              >
                <option value="server">Server</option>
                <option value="agent">Agent</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Resource path
              </label>
              <input
                type="text"
                value={lookupPath}
                onChange={(e) => setLookupPath(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
                placeholder="/my-server"
                className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm"
              />
            </div>
            <button
              onClick={handleLookup}
              disabled={ownershipLoading}
              className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              <MagnifyingGlassIcon className="h-4 w-4" />
              Look up
            </button>
          </div>

          {ownershipLoading && (
            <div className="text-sm text-gray-500 dark:text-gray-400">Loading...</div>
          )}
          {ownershipError && (
            <div className="text-sm text-red-600 dark:text-red-400">{ownershipError}</div>
          )}

          {!ownershipLoading && (ownership || lookupHasNoRecord) && (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
              {ownership ? (
                <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <dt className="text-gray-500 dark:text-gray-400">Owner</dt>
                  <dd className="text-gray-900 dark:text-gray-100">
                    {ownership.owner_username}
                    {ownership.owner_pending && (
                      <span
                        className="ml-1.5 inline-block rounded bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400"
                        title="This owner hasn't signed in yet -- ownership will finalize automatically once they do."
                      >
                        pending sign-in
                      </span>
                    )}
                  </dd>
                  <dt className="text-gray-500 dark:text-gray-400">Team</dt>
                  <dd className="text-gray-900 dark:text-gray-100">{ownership.owner_team || '-'}</dd>
                  <dt className="text-gray-500 dark:text-gray-400">Backup owner</dt>
                  <dd className="text-gray-900 dark:text-gray-100">{ownership.backup_owner || '-'}</dd>
                  {ownership.transferred_from && (
                    <>
                      <dt className="text-gray-500 dark:text-gray-400">Transferred from</dt>
                      <dd className="text-gray-900 dark:text-gray-100">
                        {ownership.transferred_from} on{' '}
                        {ownership.transferred_at && new Date(ownership.transferred_at).toLocaleString()}
                      </dd>
                    </>
                  )}
                </dl>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  This resource has no ownership record yet -- assigning an owner below will create one.
                </p>
              )}

              <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {ownership ? 'Transfer ownership' : 'Assign owner'}
                </h4>
                <div className="flex items-end gap-3">
                  <div className="w-56">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      New owner (username)
                    </label>
                    <SearchableSelect
                      options={newOwnerOptions}
                      value={newOwner}
                      onChange={setNewOwner}
                      placeholder="Search users, or type a name and press Enter..."
                      isLoading={iamUsersLoading}
                      maxHeightClass="max-h-[70vh]"
                      allowCustom
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      New team (optional)
                    </label>
                    <input
                      type="text"
                      value={newTeam}
                      onChange={(e) => setNewTeam(e.target.value)}
                      className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm"
                    />
                  </div>
                  <button
                    onClick={handleTransfer}
                    disabled={transferring}
                    className="px-3 py-2 bg-gray-800 dark:bg-gray-600 text-white rounded-md text-sm hover:bg-gray-900 disabled:opacity-50"
                  >
                    Transfer
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'audit' && (
        <div className="space-y-4">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Resource path
              </label>
              <input
                type="text"
                value={auditResourcePath}
                onChange={(e) => setAuditResourcePath(e.target.value)}
                placeholder="/my-server"
                className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Event type
              </label>
              <select
                value={auditEventType}
                onChange={(e) => setAuditEventType(e.target.value)}
                className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm"
              >
                <option value="">All</option>
                <option value="ownership_created">Ownership created</option>
                <option value="ownership_transferred">Ownership transferred</option>
                <option value="approval_requested">Approval requested</option>
                <option value="approval_decided">Approval decided</option>
                <option value="policy_violation">Policy violation</option>
                <option value="lifecycle_status_changed">Lifecycle status changed</option>
                <option value="asset_retired">Asset retired</option>
              </select>
            </div>
            <button
              onClick={fetchAudit}
              disabled={auditLoading}
              className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Refresh
            </button>
          </div>

          {auditError && (
            <div className="text-sm text-red-600 dark:text-red-400">{auditError}</div>
          )}

          <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">
                    Timestamp
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">
                    Event
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">
                    Resource
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">
                    Actor
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
                {auditEntries.map((entry) => (
                  <tr key={entry.entry_id}>
                    <td className="px-3 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {new Date(entry.timestamp).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{entry.event_type}</td>
                    <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                      {entry.resource_type}: {entry.resource_path}
                    </td>
                    <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{entry.actor}</td>
                  </tr>
                ))}
                {!auditLoading && auditEntries.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-gray-500 dark:text-gray-400">
                      No governance audit entries found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'policies' && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Create policy
            </h4>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={newPolicyName}
                  onChange={(e) => setNewPolicyName(e.target.value)}
                  className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Rule type
                </label>
                <select
                  value={newPolicyRuleType}
                  onChange={(e) => setNewPolicyRuleType(e.target.value as PolicyRuleType)}
                  className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm"
                >
                  <option value="naming">Naming</option>
                  <option value="metadata_required">Metadata required</option>
                  <option value="approval_required_for">Approval required for</option>
                  <option value="group_auto_mapping">Group auto-mapping</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Enforcement
                </label>
                <select
                  value={newPolicyEnforcementMode}
                  onChange={(e) => setNewPolicyEnforcementMode(e.target.value as PolicyEnforcementMode)}
                  className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm"
                >
                  <option value="review">Review (flag only)</option>
                  <option value="block">Block</option>
                </select>
              </div>
              {newPolicyRuleType === 'naming' ? (
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Regex pattern
                  </label>
                  <input
                    type="text"
                    value={newPolicyPattern}
                    onChange={(e) => setNewPolicyPattern(e.target.value)}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm font-mono"
                  />
                </div>
              ) : newPolicyRuleType === 'metadata_required' ? (
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Required fields (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={newPolicyRequiredFields}
                    onChange={(e) => setNewPolicyRequiredFields(e.target.value)}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm"
                  />
                </div>
              ) : newPolicyRuleType === 'group_auto_mapping' ? (
                <>
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Match tags (comma-separated, any one matches)
                    </label>
                    <input
                      type="text"
                      value={newPolicyMatchTags}
                      onChange={(e) => setNewPolicyMatchTags(e.target.value)}
                      placeholder="jab-mcp"
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm"
                    />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Target group
                    </label>
                    <input
                      type="text"
                      value={newPolicyTargetGroup}
                      onChange={(e) => setNewPolicyTargetGroup(e.target.value)}
                      placeholder="jab-mcp-only"
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2 pb-2">
                    <input
                      type="checkbox"
                      id="new-policy-shareable"
                      checked={newPolicyShareable}
                      onChange={(e) => setNewPolicyShareable(e.target.checked)}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                    <label
                      htmlFor="new-policy-shareable"
                      className="text-xs font-medium text-gray-500 dark:text-gray-400"
                    >
                      Shareable across zones
                    </label>
                  </div>
                </>
              ) : (
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Resource types (comma-separated, blank = server and agent)
                  </label>
                  <input
                    type="text"
                    value={newPolicyResourceTypes}
                    onChange={(e) => setNewPolicyResourceTypes(e.target.value)}
                    placeholder="server"
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm"
                  />
                </div>
              )}
              <button
                onClick={handleCreatePolicy}
                disabled={creatingPolicy}
                className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                <PlusIcon className="h-4 w-4" />
                Create
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {newPolicyRuleType === 'approval_required_for'
                ? "Takes effect once the backend's governance approval workflow flag is enabled."
                : newPolicyRuleType === 'group_auto_mapping'
                ? "Takes effect once the backend's governance_group_auto_mapping_enabled flag is " +
                  "enabled (AGENTGOV-163). Even then, a matching tag only auto-assigns a server " +
                  "when its owner is ALREADY an approved member of the target group -- it never " +
                  'grants new access on its own, only automates the manual add-to-group step for ' +
                  'an already-vetted owner. If the owner is not yet approved, the match is held ' +
                  "as pending and completes automatically once they are. \"Shareable across zones\" " +
                  'controls whether this zone may ever be combined with other zones in an owner\'s ' +
                  '"share across my zones" request -- leave unchecked for a zone that must stay ' +
                  'single-purpose (e.g. a finance-only zone) regardless of what an owner requests. ' +
                  'Enforcement mode above is not used for this rule type.'
                : "Naming and metadata-required policies only take effect once the backend's " +
                  'governance policy enforcement flag is enabled. Until then, creating one here ' +
                  'has no effect on registrations.'}
            </p>
          </div>

          {policiesLoading && (
            <div className="text-sm text-gray-500 dark:text-gray-400">Loading...</div>
          )}
          {policiesError && (
            <div className="text-sm text-red-600 dark:text-red-400">{policiesError}</div>
          )}

          <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">
                    Name
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">
                    Rule type
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">
                    Enforcement
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">
                    Enabled
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
                {policies.map((policy) => (
                  <tr key={policy.policy_id}>
                    <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{policy.name}</td>
                    <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{policy.rule_type}</td>
                    <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                      {policy.enforcement_mode}
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => handleTogglePolicy(policy)}
                        className={`px-2 py-1 rounded text-xs ${
                          policy.enabled
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                        }`}
                      >
                        {policy.enabled ? 'Enabled' : 'Disabled'}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => handleDeletePolicy(policy)}
                        className="text-red-600 dark:text-red-400 hover:text-red-800"
                        aria-label={`Delete policy ${policy.name}`}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {!policiesLoading && policies.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-gray-500 dark:text-gray-400">
                      No governance policies defined.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'approvals' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Pending registrations awaiting review. A resource only appears here when the
              approval workflow is enabled and a matching policy requires it.
            </p>
            <button
              onClick={fetchApprovals}
              disabled={approvalsLoading}
              className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Refresh
            </button>
          </div>

          {approvalsError && (
            <div className="text-sm text-red-600 dark:text-red-400">{approvalsError}</div>
          )}

          <div className="space-y-3">
            {approvalRequests.map((request) => (
              <div
                key={request.request_id}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {request.resource_type}: {request.resource_path}
                    </span>
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                      requested by {request.requested_by} on{' '}
                      {new Date(request.requested_at).toLocaleString()}
                    </span>
                    {request.payload_snapshot?.tags && request.payload_snapshot.tags.length > 0 && (
                      <TagList
                        tags={request.payload_snapshot.tags}
                        accent="neutral"
                        prefix="#"
                        className="mt-2"
                      />
                    )}
                  </div>
                </div>
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Reason (required to reject)
                    </label>
                    <input
                      type="text"
                      value={rejectReasons[request.request_id] || ''}
                      onChange={(e) =>
                        setRejectReasons((prev) => ({ ...prev, [request.request_id]: e.target.value }))
                      }
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm"
                    />
                  </div>
                  <button
                    onClick={() => handleApprove(request)}
                    disabled={decidingRequestId === request.request_id}
                    className="px-3 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 disabled:opacity-50"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleReject(request)}
                    disabled={decidingRequestId === request.request_id}
                    className="px-3 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 disabled:opacity-50"
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
            {!approvalsLoading && approvalRequests.length === 0 && (
              <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
                No pending approval requests.
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'cross-tenant' && isAdmin && (() => {
        const filtered = crossTenantGrants.filter((grant) => {
          const needle = crossTenantFilter.trim().toLowerCase();
          if (!needle) return true;
          return (
            grant.user_id.toLowerCase().includes(needle) ||
            (grant.server_name || grant.resource_path).toLowerCase().includes(needle)
          );
        });
        // Grouped by the server's own tenant so each company gets its own
        // table, sorted alphabetically -- new tenants get a section
        // automatically, no code change needed per company.
        const groups = new Map<string, CrossTenantGrantSummary[]>();
        for (const grant of filtered) {
          const tenant = grant.server_tenant_id || 'Unknown';
          const existing = groups.get(tenant);
          if (existing) {
            existing.push(grant);
          } else {
            groups.set(tenant, [grant]);
          }
        }
        // The section list comes from which tenants actually HAVE servers,
        // not just which already have a grant -- otherwise a company with
        // zero grants so far would have no section to click "Add grant"
        // under in the first place.
        const serverTenants = new Set(
          (servers || [])
            .filter((s) => s.type === 'server')
            .map((s) => s.tenant_id || 'Unknown')
        );
        const sortedTenants = [...new Set([...serverTenants, ...groups.keys()])].sort((a, b) =>
          a.localeCompare(b)
        );
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Every active cross-tenant grant, across every server -- a narrow (user,
                server) exception to the tenant wall, never a role or group. Each tenant
                below grants into only its own servers -- pick the server first and
                there is no ambiguity about which company's wall is being crossed.
              </p>
              <button
                onClick={fetchCrossTenantGrants}
                disabled={crossTenantLoading}
                className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50 shrink-0"
              >
                <ArrowPathIcon className="h-4 w-4" />
                Refresh
              </button>
            </div>

            {crossTenantError && (
              <div className="text-sm text-red-600 dark:text-red-400">{crossTenantError}</div>
            )}

            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {filtered.length} active grant{filtered.length === 1 ? '' : 's'}
                {sortedTenants.length > 1 ? ` across ${sortedTenants.length} tenants` : ''}
              </h3>
              <div className="relative w-64">
                <MagnifyingGlassIcon className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={crossTenantFilter}
                  onChange={(e) => setCrossTenantFilter(e.target.value)}
                  placeholder="Filter by person or server..."
                  className="w-full pl-8 rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm"
                />
              </div>
            </div>

            {sortedTenants.map((tenant) => {
              const grants = groups.get(tenant) || [];
              return (
                <div
                  key={tenant}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                >
                  <div className="flex items-center justify-between gap-2 p-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${tenantBadgeColor(tenant)}`}
                      >
                        {tenant}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {grants.length} grant{grants.length === 1 ? '' : 's'} into this tenant's servers
                      </span>
                    </div>
                    <button
                      onClick={() => setAddGrantTenant((v) => (v === tenant ? null : tenant))}
                      className="flex items-center gap-1 px-2.5 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-600 shrink-0"
                    >
                      <PlusIcon className="h-3.5 w-3.5" />
                      Add grant
                    </button>
                  </div>

                  {addGrantTenant === tenant && (
                    <div className="p-3 space-y-3 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Server (this tenant's servers only)
                          </label>
                          <SearchableSelect
                            options={grantServerOptions}
                            value={grantServerPath}
                            onChange={setGrantServerPath}
                            placeholder="Search servers..."
                            isLoading={serversLoading}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Person (username)
                          </label>
                          <SearchableSelect
                            options={newOwnerOptions}
                            value={grantUserId}
                            onChange={setGrantUserId}
                            placeholder="Search users, or type a name and press Enter..."
                            isLoading={iamUsersLoading}
                            allowCustom
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Their company (optional, for display only)
                          </label>
                          <input
                            type="text"
                            value={grantTenantLabel}
                            onChange={(e) => setGrantTenantLabel(e.target.value)}
                            placeholder="e.g. Geidi"
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Reason
                          </label>
                          <input
                            type="text"
                            value={grantReason}
                            onChange={(e) => setGrantReason(e.target.value)}
                            placeholder="Why does this person need this server?"
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setAddGrantTenant(null)}
                          disabled={isSavingGrant}
                          className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => void handleAddCrossTenantGrant()}
                          disabled={
                            isSavingGrant ||
                            !grantServerPath.trim() ||
                            !grantUserId.trim() ||
                            !grantReason.trim()
                          }
                          className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
                        >
                          {isSavingGrant ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  )}

                  {grants.length === 0 ? (
                    <div className="text-xs text-gray-500 dark:text-gray-400 text-center py-4">
                      No grants into this tenant's servers yet.
                    </div>
                  ) : (
                  <div className="max-h-[32rem] overflow-y-auto">
                    <table className="w-full text-sm table-fixed">
                      <colgroup>
                        <col className="w-[14%]" />
                        <col className="w-[10%]" />
                        <col className="w-[14%]" />
                        <col className="w-[6%]" />
                        <col className="w-[24%]" />
                        <col className="w-[12%]" />
                        <col className="w-[10%]" />
                        <col className="w-[10%]" />
                        <col className="w-[36px]" />
                      </colgroup>
                      <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium text-gray-500 dark:text-gray-400 truncate">Person</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-500 dark:text-gray-400 truncate">Their company</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-500 dark:text-gray-400 truncate">Server</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-500 dark:text-gray-400 truncate">Tools</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-500 dark:text-gray-400 truncate">Reason</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-500 dark:text-gray-400 truncate">Granted by</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-500 dark:text-gray-400 truncate">Granted on</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-500 dark:text-gray-400 truncate">Last used</th>
                          <th className="px-3 py-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {grants.map((grant) => {
                          const key = `${grant.user_id}::${grant.resource_path}`;
                          return (
                            <tr
                              key={key}
                              className="border-b border-gray-100 dark:border-gray-800 last:border-0"
                            >
                              <td
                                className="px-3 py-2 font-medium text-gray-900 dark:text-white truncate"
                                title={grant.user_id}
                              >
                                {grant.user_id}
                              </td>
                              <td className="px-3 py-2 text-gray-700 dark:text-gray-300 truncate">
                                {grant.user_tenant_label || '—'}
                              </td>
                              <td
                                className="px-3 py-2 text-gray-700 dark:text-gray-300 truncate"
                                title={grant.server_name || grant.resource_path}
                              >
                                {grant.server_name || grant.resource_path}
                              </td>
                              <td className="px-3 py-2 text-gray-700 dark:text-gray-300 truncate">
                                {grant.num_tools}
                              </td>
                              <td
                                className="px-3 py-2 text-gray-700 dark:text-gray-300 truncate"
                                title={grant.reason}
                              >
                                {grant.reason}
                              </td>
                              <td
                                className="px-3 py-2 text-gray-500 dark:text-gray-400 truncate"
                                title={grant.granted_by}
                              >
                                {grant.granted_by}
                              </td>
                              <td className="px-3 py-2 text-gray-500 dark:text-gray-400 truncate">
                                {new Date(grant.granted_at).toLocaleString()}
                              </td>
                              <td className="px-3 py-2 text-gray-500 dark:text-gray-400 truncate">
                                {grant.last_used_at
                                  ? new Date(grant.last_used_at).toLocaleString()
                                  : 'Never'}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <button
                                  onClick={() => void handleRevokeCrossTenantGrant(grant)}
                                  disabled={revokingGrantKey === key}
                                  className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded transition-colors disabled:opacity-50"
                                  title={`Revoke ${grant.user_id}'s access`}
                                  aria-label={`Revoke ${grant.user_id}'s access`}
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  )}
                </div>
              );
            })}

            {sortedTenants.length === 0 && (
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-500 dark:text-gray-400 text-center py-6">
                No servers are registered yet, so there is nothing to grant cross-tenant access to.
              </div>
            )}
            {sortedTenants.length > 0 &&
              !crossTenantLoading &&
              filtered.length === 0 &&
              crossTenantGrants.length > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  No grants match that filter.
                </p>
              )}
          </div>
        );
      })()}
    </div>
  );
};

export default GovernanceDashboard;
